import CommonProjectInvoiceDetails from "modules/billing/components/CommonProjectInvoiceDetails";

import Button from "~/components/buttons/Button";
import { SelectPlain } from "~/components/formElements/Select/Select.tsx";
import Hint from "~/components/Hint";
import WordIcon from "~/components/icons/WordIcon";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import type { InvoiceTypeId} from "~/modules/billing/api/invoiceType.ts";
import {invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import InvoiceDetailsElement from "~/modules/billing/components/InvoiceDetailsElement";
import type { UnprocessedInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import { useInvoiceDownload } from "~/modules/billing/hooks/useInvoiceDownload.ts";
import { useInvoiceTypeSelectOptions } from "~/modules/billing/hooks/useInvoiceTypeSelectOptions.ts";
import type { User, UserWithConfidentialInformation } from "~/modules/user/api/user/userTypes.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";

type Props = {
	allUsersData: User[] | UserWithConfidentialInformation[];
	onClose: () => void;
	onStartBillingProcessClick: () => void;
	pendingInvoiceData: UnprocessedInvoicesIndexTableData;
	selectedInvoiceTypeId: InvoiceTypeId;
	setSelectedInvoiceTypeId: (id: InvoiceTypeId) => void;
	userCanManageInvoices: boolean
};

const PendingInvoiceDetails = ({
	allUsersData,
	onClose,
	onStartBillingProcessClick,
	pendingInvoiceData,
	selectedInvoiceTypeId,
	setSelectedInvoiceTypeId,
	userCanManageInvoices,
}: Props) => {
	const { isGenerating, handleDownload } = useInvoiceDownload(pendingInvoiceData.id);
	const isDeletable = pendingInvoiceData.invoiceTotalCents === 0;

	const invoiceTypeSelectOptions = useInvoiceTypeSelectOptions();
	const isAdvanceInvoice = pendingInvoiceData.invoiceTypeId === invoiceTypeId.AdvanceInvoice;

	return <>
		<SidebarHeader>
			<SidebarHeaderHeadline>Rechnungsdetails</SidebarHeaderHeadline>
		</SidebarHeader>
		<div className="grow overflow-auto px-7 py-2">
			{pendingInvoiceData.hasOverbookedOrder && <><Hint size="sm"
															  theme="error">Das Kostenvolumen der Bestellung ist
				überbucht.</Hint>
				<div className="h-2" />
			</>}
			{isDeletable && <Hint size="sm"
								  theme="warning">Der Abrechnungsvorgang kann nicht gestartet werden, da keine
				Erfassungen in dem Leistungszeitraum vorhanden sind. Die offene Rechnung kann daher gelöscht
				werden.</Hint>}
			<CommonProjectInvoiceDetails allUsersData={allUsersData}
										 invoiceData={pendingInvoiceData}
										 staffingsClosingStatuses={pendingInvoiceData.staffingsClosingStatuses}>
				<InvoiceDetailsElement label="Abrechnungsart"
									   truncate={false}>
					<div className="w-full">
						{!isAdvanceInvoice ? <SelectPlain optionsData={invoiceTypeSelectOptions}
									 value={selectedInvoiceTypeId}
									 onChange={setSelectedInvoiceTypeId} />:
							<div className="font-bold"> Vorabrechnung / Abrechnungskontingent</div>}
					</div>
				</InvoiceDetailsElement>
				<InvoiceDetailsElement label="Leistungszeitraum">
					{formatSmartDateRange(pendingInvoiceData.deliveryStartDate, pendingInvoiceData.deliveryEndDate, {
						month: "MMMM",
						year: " yyyy",
					})}
				</InvoiceDetailsElement>
			</CommonProjectInvoiceDetails>
		</div>
		<SidebarFooter>
			<div className="mr-auto">
				<Button theme="none"
						onClick={handleDownload}><WordIcon className="size-5" />Voransicht{isGenerating &&
					<LoadingSpinner scale={0.7}
									theme="neutral" />}</Button>
			</div>
			{!isDeletable && userCanManageInvoices && <Button theme="primary"
															  onClick={onStartBillingProcessClick}
			>
				Abrechnungsvorgang starten
			</Button>}
			<Button theme="none"
					onClick={onClose}>
				{userCanManageInvoices ? "Abbrechen" : "Schließen"}
			</Button>
		</SidebarFooter>
	</>;
};

export default PendingInvoiceDetails;